import {call, delay, put, takeEvery} from '@redux-saga/core/effects';
import accountApi from 'Agent/Accounts/api/accountApi';
import {
  AccountActionType,
  changeAccounts,
  deletePolicyInformationReport,
  getAccounts,
  getPolicyInformationReports,
} from 'Agent/Accounts/ducks/account';
import {Account} from 'Agent/Accounts/models/Account';
import {AsyncResult, fromResult, Loading, LoadingAdditional} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {all, takeLatest} from 'redux-saga/effects';
import {toSortQuerystringParams} from 'Shared/components/PagedView/Table/Sort';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {prop} from 'Shared/utilities/prop';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';
import {changePolicyInformationReports} from '../ducks/account';

function* getAccountsFromApi(action: ReturnType<typeof getAccounts>) {
  const {page, encryptedIdentifier, isNewYork, agentNumbers, filter, sort} = action.payload;

  if (filter) {
    const {name, number, agentOfRecordNumber, servicingAgentNumber} = filter;
    if (name !== '' || number !== '' || agentOfRecordNumber !== '' || servicingAgentNumber !== '') yield delay(1500);
  }

  if (page) {
    page.links.forEach(x => {
      if (!x.href.includes('api/employer')) {
        x.href = 'api/employer' + x.href;
      }
    });

    yield put(changeAccounts(new LoadingAdditional(page)));
  } else {
    yield put(changeAccounts(new Loading(page)));
  }
  const parameters = addFilterParameters(filter, 'post').slice(1);
  let bodyParams = new URLSearchParams();
  if (parameters) {
    parameters.split('&').forEach(value => {
      let keyValue = value?.trim().split('=');
      bodyParams?.append(keyValue[0]?.trim(), keyValue[1]?.trim());
    });
  }
  if (sort && toSortQuerystringParams(sort)) {
    bodyParams.append('sortOrder', (sort && toSortQuerystringParams(sort)).sortOrder);
    bodyParams.append('sortColumn', (sort && toSortQuerystringParams(sort)).sortColumn);
  }
  bodyParams.append('agentNumbers', agentNumbers.map(prop('value')).join(','));
  bodyParams.append('isNewYork', JSON.stringify(isNewYork));
  bodyParams.append('encryptedIdentifier', encryptedIdentifier);
  if (page) bodyParams.append('pageNumber', (page?.currentPageNumber + 1).toString());
  const result: AsyncResult<Page<Account>> = yield page
    ? call(mergeWithNextPage, {existingPage: page}, 1, bodyParams)
    : call(accountApi.getAccounts, bodyParams);
  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changeAccounts(value)),
      value => put(changeAccounts(value))
    )
  );
}

function* watchFetchAccounts() {
  yield takeLatest(AccountActionType.GET_ACCOUNTS, getAccountsFromApi);
}

function* getPolicyInformationReportsFromApi(action: ReturnType<typeof getPolicyInformationReports>) {
  const {encryptedIdentifier, userId} = action.payload;
  yield getApiCallGenerator(
    () => accountApi.getPolicyInformationReports(encryptedIdentifier, userId),
    changePolicyInformationReports
  )(action);
}

function* getPolicyInformationReportsSaga() {
  yield takeLatest(AccountActionType.GET_POLICY_INFORMATION_REPORTS, getPolicyInformationReportsFromApi);
}

function* deletePolicyInformationReportApi(action: ReturnType<typeof deletePolicyInformationReport>) {
  const {encryptedIdentifier, userId, reportId, eisFlag} = action.payload;
  yield call(accountApi.deletePolicyInformationReport, encryptedIdentifier, userId, reportId, eisFlag);

  yield put(getPolicyInformationReports(encryptedIdentifier, userId));
}

function* deletePolicyInformationReportSaga() {
  yield takeEvery(AccountActionType.REMOVE_POLICY_INFORMATION_REPORTS, deletePolicyInformationReportApi);
}

export default function*() {
  yield all([watchFetchAccounts(), getPolicyInformationReportsSaga(), deletePolicyInformationReportSaga()]);
}
